import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../../components/layout/layout";
import { fetchArticleBySlug, fetchPublishedArticles } from "../../lib/articles";
import ArticleCard from "../../components/articles/ArticleCard";
import TableOfContents from "../../components/articles/TableOfContents";
import { supabase } from "../../lib/supabase";
import Button from "../../components/ui/button";
import { Helmet } from "react-helmet";
import { WebPageSchema } from "../../lib/utils/schema-utils";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [article, setArticle] = useState<any>(null);
  const [latestArticles, setLatestArticles] = useState<any[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAdmin = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data: userData } = await supabase
        .from("users")
        .select("role")
        .eq("id", user.id)
        .single();
      if (userData?.role === "admin") {
        setIsAdmin(true);
      }
    };

    const load = async () => {
      if (!slug) return;
      setIsLoading(true);
      const a = await fetchArticleBySlug(slug);
      const latest = await fetchPublishedArticles();
      setArticle(a);
      setLatestArticles(latest.filter((art) => art.slug !== slug).slice(0, 3));
      setIsLoading(false);
    };

    checkAdmin();
    load();
  }, [slug]);

  if (isLoading) {
    return <div className="p-10 text-center text-neutral-600">Chargement...</div>;
  }

  if (!article) {
    return (
      <Layout title="Article introuvable">
        <div className="py-32 text-center text-neutral-500 text-xl">
          😕 Désolé, cet article n'existe pas ou a été supprimé.
        </div>
      </Layout>
    );
  }

  const getCategoryColor = (category?: string) => {
    const colors: { [key: string]: string } = {
      'Recrutement': 'bg-blue-100 text-blue-800',
      'Marque employeur': 'bg-pink-100 text-pink-800',
      'RH & Management': 'bg-purple-100 text-purple-800',
      'Onboarding': 'bg-green-100 text-green-800',
      'Culture d\'entreprise': 'bg-yellow-100 text-yellow-800',
      'Innovation RH': 'bg-indigo-100 text-indigo-800',
      'Général': 'bg-neutral-100 text-neutral-800'
    };
    return colors[category || 'Général'] || colors['Général'];
  };

  return (
    <Layout title={article.title} description={article.excerpt || article.title}>
      <Helmet>
        <title>{article.title} | recruteurs.io</title>
        <meta name="description" content={article.excerpt || article.title} />
        <link rel="canonical" href={`https://recruteurs.io/ressources/articles/${article.slug}`} />

        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.excerpt || article.title} />
        <meta property="og:image" content={article.image_url || 'https://recruteurs.io/default.jpg'} />
        <meta property="og:url" content={`https://recruteurs.io/ressources/articles/${article.slug}`} />
        <meta property="og:type" content="article" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={article.excerpt || article.title} />
        <meta name="twitter:image" content={article.image_url || 'https://recruteurs.io/default.jpg'} />
      </Helmet>

      <WebPageSchema
        page={{
          title: article.title,
          description: article.excerpt || article.title,
          url: `https://recruteurs.io/ressources/articles/${article.slug}`,
          image: article.image_url,
          publishedAt: article.created_at,
        }}
      />

      <div className="container-custom py-16">
        {article.image_url && (
          <div className="relative mb-10">
            <img src={article.image_url} alt={article.title} className="w-full rounded-xl shadow-lg max-h-[450px] object-cover" />
            {article.category && (
              <div className="absolute top-6 left-6">
                <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium ${getCategoryColor(article.category)}`}>{article.category}</span>
              </div>
            )}
          </div>
        )}

        <div className="mb-10">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-4xl font-bold mb-2">{article.title}</h1>
              <p className="text-neutral-600">{new Date(article.created_at).toLocaleDateString("fr-FR")}</p>
            </div>
            {isAdmin && (
              <Button variant="outline" onClick={() => navigate(`/ressources/articles/${slug}/edit`)} className="shrink-0">
                ✏️ Modifier l'article
              </Button>
            )}
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-12">
          <div className="lg:w-2/3 prose max-w-none" dangerouslySetInnerHTML={{ __html: article.content }} />

          <div className="lg:w-1/3">
            <div className="sticky top-20 space-y-8">
              <TableOfContents html={article.content} />

              <div className="rounded-xl p-6 bg-gradient-to-br from-pink-100 via-yellow-100 to-blue-100 shadow">
                <h3 className="text-xl font-bold mb-2">Pimp My Job 🚀</h3>
                <p className="text-neutral-700 mb-4">Générez des offres d'emploi irrésistibles grâce à l'IA.</p>
                <a href="https://pimpmyjob.recruteurs.io" className="inline-block bg-black text-white px-4 py-2 rounded hover:bg-neutral-800 transition">
                  Tester gratuitement
                </a>
              </div>

              <div className="rounded-xl p-6 bg-gradient-to-r from-blue-100 to-purple-100 shadow">
                <h3 className="text-xl font-bold mb-2">Mapping RH 🧠</h3>
                <p className="text-neutral-700 mb-4">Évaluez la compatibilité entre vos candidats et vos équipes.</p>
                <a href="/mapping-rh" className="inline-block bg-black text-white px-4 py-2 rounded hover:bg-neutral-800 transition">
                  Tester le mapping
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-20">
          <h3 className="text-2xl font-bold mb-6">Derniers articles</h3>
          <div className="grid md:grid-cols-3 gap-6">
            {latestArticles.map((a) => (
              <div key={a.id} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-200">
                {a.image_url && (
                  <div className="aspect-[16/9] overflow-hidden relative">
                    <img src={a.image_url} alt={a.title} className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-300" />
                    {a.category && (
                      <div className="absolute top-4 left-4">
                        <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getCategoryColor(a.category)}`}>{a.category}</span>
                      </div>
                    )}
                  </div>
                )}
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors line-clamp-2">{a.title}</h3>
                  <p className="text-neutral-600 mb-4 line-clamp-3">{a.excerpt || a.content}</p>
                  <div className="text-sm text-neutral-500">{new Date(a.created_at).toLocaleDateString("fr-FR")}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}